import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MenuItem, MenuItemsService } from '../menu-items.service';
import { MenuItemFormDialogComponent } from '../menu-item-form-dialog/menu-item-form-dialog.component';
import { HasPermissionDirective } from '../../../../../core/directives/has-permission.directive';
import { ConfirmationService } from '../../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../../shared/shared.module';

@Component({
  selector: 'app-menu-items-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  template: `
    <section class="config-page">
      <app-card variant="default" padding="none" class="config-table-card">
        <div *ngIf="loading" class="loading-container">
          <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
          <p>Loading menu items...</p>
        </div>

        <div *ngIf="!loading && items.length === 0" class="empty-state">
          <div class="empty-state__icon"><mat-icon>restaurant_menu</mat-icon></div>
          <h3>No menu items yet</h3>
          <p>Add sellable items like Chicken Fried Rice with price, cost and category.</p>
          <button mat-flat-button color="primary" (click)="onCreate()" *appHasPermission="'menu_item.create'">
            <mat-icon>add</mat-icon> Add Menu Item
          </button>
        </div>

        <div *ngIf="!loading && items.length > 0" class="table-container">
          <table mat-table [dataSource]="items" class="modern-table">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Item</th>
              <td mat-cell *matCellDef="let i">
                <div class="entity-cell">
                  <div class="entity-cell__icon">
                    <img *ngIf="i.image" [src]="i.image" alt="" />
                    <mat-icon *ngIf="!i.image">restaurant_menu</mat-icon>
                  </div>
                  <div class="entity-cell__details">
                    <strong class="entity-cell__name">{{ i.name }}</strong>
                    <span class="entity-cell__sub" *ngIf="i.category_name">{{ i.category_name }}</span>
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="price">
              <th mat-header-cell *matHeaderCellDef>Price</th>
              <td mat-cell *matCellDef="let i">{{ i.price }}</td>
            </ng-container>

            <ng-container matColumnDef="cost">
              <th mat-header-cell *matHeaderCellDef>Cost</th>
              <td mat-cell *matCellDef="let i">{{ i.cost }}</td>
            </ng-container>

            <ng-container matColumnDef="tax">
              <th mat-header-cell *matHeaderCellDef>Tax</th>
              <td mat-cell *matCellDef="let i">{{ i.tax_name || '—' }}</td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let i">
                <span class="status-badge" [class.active]="i.is_active" [class.inactive]="!i.is_active">
                  <span class="status-indicator"></span>{{ i.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef>Actions</th>
              <td mat-cell *matCellDef="let i">
                <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="More actions" class="action-menu-trigger">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu" class="action-menu">
                  <button mat-menu-item (click)="onEdit(i)" *appHasPermission="'menu_item.update'">
                    <mat-icon>edit</mat-icon><span>Edit</span>
                  </button>
                  <button mat-menu-item (click)="onToggle(i)" *appHasPermission="'menu_item.update'">
                    <mat-icon>{{ i.is_active ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                    <span>{{ i.is_active ? 'Deactivate' : 'Activate' }}</span>
                  </button>
                  <mat-divider></mat-divider>
                  <button mat-menu-item (click)="onDelete(i)" class="danger-action" *appHasPermission="'menu_item.delete'">
                    <mat-icon>delete</mat-icon><span>Delete</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>

          <mat-paginator
            [length]="total"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            [showFirstLastButtons]="true"
            aria-label="Menu item pagination"
            (page)="handlePageEvent($event)"
          ></mat-paginator>
        </div>
      </app-card>
    </section>
  `,
  styleUrls: ['../../config-list.shared.scss'],
})
export class MenuItemsListComponent implements OnInit, OnDestroy {
  items: MenuItem[] = [];
  loading = false;
  displayedColumns = ['name', 'price', 'cost', 'tax', 'status', 'actions'];

  total = 0;
  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private service: MenuItemsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.pageHeader.set({
      title: 'Menu Items',
      icon: 'restaurant_menu',
      backRoute: '/dashboard',
      actions: [
        { label: 'Refresh', icon: 'refresh', variant: 'stroked', permission: 'menu_item.read', disabled: () => this.loading, handler: () => this.load() },
        { label: 'Add Menu Item', icon: 'add', variant: 'primary', permission: 'menu_item.create', handler: () => this.onCreate() },
      ],
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  load(): void {
    this.loading = true;
    this.service.getPage({ page: this.pageIndex + 1, perPage: this.pageSize }).subscribe({
      next: (page) => {
        this.items = page.items;
        this.total = page.meta.total;
        this.pageSize = page.meta.perPage;
        this.pageIndex = Math.max(page.meta.currentPage - 1, 0);
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load menu items', 'Close', { duration: 3000 });
        this.loading = false;
      },
    });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.pageSize === event.pageSize && this.pageIndex === event.pageIndex) {
      return;
    }
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.load();
  }

  onCreate(): void {
    this.dialog.open(MenuItemFormDialogComponent, { width: '680px', maxHeight: '90vh', data: {} })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onEdit(i: MenuItem): void {
    this.dialog.open(MenuItemFormDialogComponent, { width: '680px', maxHeight: '90vh', data: { id: i.id } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onToggle(i: MenuItem): void {
    this.service.toggleActive(i.id).subscribe({
      next: (res) => {
        if (res.success) {
          i.is_active = !i.is_active;
          this.snackBar.open(`Menu item ${i.is_active ? 'activated' : 'deactivated'}`, 'Close', { duration: 3000 });
        }
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 }),
    });
  }

  onDelete(i: MenuItem): void {
    this.confirmationService.confirm({
      title: 'Delete menu item',
      message: `Are you sure you want to delete "${i.name}"?`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.service.delete(i.id).subscribe({
        next: (res) => {
          if (res.success) {
            this.snackBar.open('Menu item deleted', 'Close', { duration: 3000 });
            this.load();
          }
        },
        error: (error) => this.snackBar.open(error.error?.message || 'Failed to delete', 'Close', { duration: 5000 }),
      });
    });
  }
}
